import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileDown, FileSpreadsheet, Plus, RotateCcw, ShieldCheck } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MarqueObligatoire,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { ChampDate } from '../components/ui/ChampDate';
import { useToast } from '../components/ui/useToast';
import { FicheReclamation } from '../components/reclamations/FicheReclamation';
import { SaisieReclamation } from '../components/reclamations/SaisieReclamation';
import { tonStatut } from '../components/reclamations/affichage';
import { useAuth } from '../auth/useAuth';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { ErreurApi } from '../lib/api';
import { horodatageReunion } from '../lib/emargement-affichage';
import { dateReunion } from '../lib/useFormateurs';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import {
  LIMITES_RECLAMATION,
  estReclamant,
  exporterReclamations,
  periodeIncomplete,
  useDeposerReclamation,
  useMesReclamations,
  useRegistreReclamations,
  type ChoixPeriodeReclamations,
  type FiltreStatutReclamation,
  type FiltresReclamations,
  type LigneRegistre,
  type MaReclamation,
} from '../lib/useReclamations';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Réclamations (TF-482, indicateur Qualiopi 31). Une réclamation n'est jamais
 * anonyme : rattachée à la personne qui réclame, informée de sa traçabilité,
 * assignée au responsable de son antenne et suivie jusqu'à sa clôture.
 *
 * L'apprenant et le formateur y déposent et suivent les leurs ; le pôle
 * pédagogique, l'administratif et la direction y tiennent le registre de leurs
 * antennes. `?reclamation=` ouvre une réclamation (lien des notifications).
 */
export function ReclamationsPage(): JSX.Element {
  const { utilisateur } = useAuth();
  return estReclamant(utilisateur?.profil) ? <MesReclamations /> : <RegistreReclamations />;
}

// ---------------------------------------------------------------------------
// Registre (équipe)
// ---------------------------------------------------------------------------

type Filtres = Omit<FiltresReclamations, 'site'>;

const SANS_FILTRE: Filtres = {
  periode: 'TOUTES',
  du: '',
  au: '',
  statut: 'TOUS',
  categorie: '',
  responsable: '',
  recherche: '',
};

const VUES: readonly FiltreStatutReclamation[] = ['TOUS', 'OUVERTES', 'HORS_DELAI', 'CLOTUREE'];

const LIBELLES_VUE: Record<string, string> = {
  TOUS: 'Toutes',
  OUVERTES: 'À traiter',
  HORS_DELAI: 'Hors délai',
  CLOTUREE: 'Clôturées',
};

const ETAPES = [
  { titre: 'Réception et accusé', detail: 'À la personne qui a un compte, dès le dépôt ; elle sait sa réclamation tracée' },
  { titre: 'Qualification', detail: 'Catégorie, gravité et responsable' },
  { titre: 'Instruction', detail: 'Notes de suivi, réponse à la personne' },
  { titre: 'Clôture', detail: 'Réponse, conclusion et action corrective' },
  { titre: 'Amélioration continue', detail: 'Pistes reprises dans l’export (indicateur 32)' },
] as const;

function RegistreReclamations(): JSX.Element {
  const { site } = usePerimetre();
  const toast = useToast();
  const [parametres, setParametres] = useSearchParams();
  const ouverte = parametres.get('reclamation');
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [saisie, setSaisie] = useState(false);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [exportEnCours, setExportEnCours] = useState<'xlsx' | 'pdf' | null>(null);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const demandes: FiltresReclamations = { site, ...filtres, recherche };
  const { data, isLoading, isFetching, error } = useRegistreReclamations(demandes);

  useEffect(
    () => setPage(1),
    [site, taillePage, recherche, filtres.periode, filtres.du, filtres.au, filtres.statut, filtres.categorie, filtres.responsable],
  );

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const choisirPeriode = (periode: ChoixPeriodeReclamations): void =>
    setFiltres((avant) => ({
      ...avant,
      periode,
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.au ?? '') : avant.au,
    }));
  const ouvrir = (id: string | null): void =>
    setParametres(
      (avant) => {
        const prochains = new URLSearchParams(avant);
        if (id) prochains.set('reclamation', id);
        else prochains.delete('reclamation');
        return prochains;
      },
      { replace: true },
    );

  const exporter = async (format: 'xlsx' | 'pdf'): Promise<void> => {
    setErreurExport(null);
    setExportEnCours(format);
    try {
      const { nomFichier } = await exporterReclamations(format, demandes);
      toast(`${nomFichier} téléchargé`);
    } catch (refus) {
      setErreurExport(messageErreur(refus, 'L’export n’a pas pu être produit.'));
    } finally {
      setExportEnCours(null);
    }
  };

  const compteurs = data?.compteurs;
  const lignes = data?.lignes ?? [];
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const incomplete = periodeIncomplete(filtres);
  const filtreActif =
    filtres.recherche !== '' || filtres.categorie !== '' || filtres.responsable !== '' || filtres.periode !== 'TOUTES';
  const comptes: Record<string, number | undefined> = compteurs
    ? {
        TOUS: compteurs.total,
        OUVERTES: compteurs.nouvelles + compteurs.enInstruction,
        HORS_DELAI: compteurs.horsDelai,
        CLOTUREE: compteurs.cloturees,
      }
    : {};

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Réclamations"
        sousTitre="Recueil, traitement et suivi jusqu’à la clôture · preuve Qualiopi de l’indicateur 31"
        actions={
          <>
            <BoutonPrincipal onClick={() => setSaisie(true)} disabled={!data}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Saisir une réclamation
            </BoutonPrincipal>
            <BoutonSecondaire onClick={() => void exporter('xlsx')} disabled={exportEnCours !== null || lignes.length === 0 || incomplete}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'xlsx' ? 'Export…' : 'Excel'}
            </BoutonSecondaire>
            <BoutonSecondaire onClick={() => void exporter('pdf')} disabled={exportEnCours !== null || lignes.length === 0 || incomplete}>
              <FileDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'pdf' ? 'Export…' : 'PDF'}
            </BoutonSecondaire>
          </>
        }
      />

      <BarreSites />

      {erreurExport && <MessageErreur message={erreurExport} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Nouvelles"
          valeur={compteurs?.nouvelles ?? '—'}
          ton={compteurs && compteurs.nouvelles > 0 ? 'warm' : 'navy'}
          detail="À qualifier et assigner"
        />
        <Compteur
          libelle="En instruction"
          valeur={compteurs?.enInstruction ?? '—'}
          ton="blue"
          detail={compteurs ? `${compteurs.horsDelai} ouverte${compteurs.horsDelai > 1 ? 's' : ''} hors délai` : 'En cours de traitement'}
        />
        <Compteur
          libelle="Clôturées"
          valeur={compteurs?.cloturees ?? '—'}
          ton="mint"
          detail={
            compteurs
              ? `dont ${compteurs.clotureesHorsDelai} au-delà du délai visé`
              : 'Réponse apportée'
          }
        />
        <Compteur
          libelle="Délai moyen"
          valeur={compteurs?.delaiMoyen === null || !compteurs ? '—' : `${String(compteurs.delaiMoyen).replace('.', ',')} j`}
          ton={compteurs?.delaiMoyen !== null && compteurs && compteurs.delaiMoyen > compteurs.delaiCible ? 'red' : 'mint'}
          detail={compteurs ? `Objectif : ${compteurs.delaiCible} jours au plus` : 'Des réclamations clôturées'}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <Onglets
            etiquette="Statut des réclamations"
            onglets={VUES.map((cle) => ({ cle, libelle: LIBELLES_VUE[cle] ?? cle, compte: comptes[cle] }))}
            actif={filtres.statut}
            onChanger={(statut) => changer('statut', statut)}
            idPanneau="panneau-reclamations"
            actions={
              <>
                <ChampFiltre
                  valeur={filtres.periode}
                  onChange={(valeur) => choisirPeriode(valeur as ChoixPeriodeReclamations)}
                  etiquette="Période de réception"
                >
                  {(data?.options.periodes ?? [{ valeur: 'TOUTES', libelle: 'Toutes les réclamations' }]).map((option) => (
                    <option key={option.valeur} value={option.valeur}>
                      {option.libelle}
                    </option>
                  ))}
                </ChampFiltre>
                {filtres.periode === 'LIBRE' && (
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    <ChampDate libelle="Du" valeur={filtres.du} max={filtres.au || undefined} onChange={(du) => changer('du', du)} />
                    <ChampDate libelle="au" valeur={filtres.au} min={filtres.du || undefined} onChange={(au) => changer('au', au)} />
                  </div>
                )}
                <ChampFiltre valeur={filtres.categorie} onChange={(valeur) => changer('categorie', valeur)} etiquette="Catégorie">
                  <option value="">Toutes les catégories</option>
                  {data?.options.categories.map((option) => (
                    <option key={option.valeur} value={option.valeur}>
                      {option.libelle}
                    </option>
                  ))}
                </ChampFiltre>
                <ChampFiltre valeur={filtres.responsable} onChange={(valeur) => changer('responsable', valeur)} etiquette="Responsable">
                  <option value="">Tous les responsables</option>
                  <option value="MOI">Celles dont je suis responsable</option>
                </ChampFiltre>
                <ChampRecherche
                  valeur={filtres.recherche}
                  onChange={(valeur) => changer('recherche', valeur)}
                  etiquette="Rechercher une réclamation"
                  placeholder="Référence, réclamant, objet…"
                />
                {filtreActif && (
                  <button
                    type="button"
                    onClick={() => setFiltres((avant) => ({ ...SANS_FILTRE, statut: avant.statut }))}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Réinitialiser
                  </button>
                )}
              </>
            }
          />

          <PanneauOnglet id="panneau-reclamations" actif={filtres.statut} className="space-y-4">
            {incomplete && filtres.du && filtres.au && filtres.du > filtres.au && (
              <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
            )}
            {isLoading && <Chargement libelle="Chargement du registre…" />}
            {error && <MessageErreur message={messageErreur(error, 'Le registre n’a pas pu être chargé.')} />}
            {data && lignes.length === 0 && (
              <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
                {filtreActif || filtres.statut !== 'TOUS'
                  ? 'Aucune réclamation ne correspond aux filtres.'
                  : 'Aucune réclamation dans ce périmètre.'}
              </p>
            )}
            {data && lignes.length > 0 && (
              <div className={`overflow-hidden rounded-2xl bg-white shadow-card transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
                <div className="border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
                  <h2 className="font-display font-bold text-codialis-navy">Registre des réclamations</h2>
                </div>
                <ul className="divide-y divide-gray-100">
                  {affichees.map((ligne) => (
                    <li key={ligne.id}>
                      <LigneReclamation ligne={ligne} onOuvrir={() => ouvrir(ligne.id)} />
                    </li>
                  ))}
                </ul>
                <Pagination
                  page={page}
                  pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
                  total={lignes.length}
                  affiches={affichees.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={setTaillePage}
                  uniteLabel="réclamation"
                />
              </div>
            )}
          </PanneauOnglet>
        </div>

        <div className="min-w-0">
          <Carte titre="Traitement d’une réclamation" sansBordure>
            <ol className="space-y-4 px-4 py-4 sm:px-6">
              {ETAPES.map((etape, rang) => (
                <li key={etape.titre} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-codialis-gray"
                  >
                    {rang + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-codialis-navy">{etape.titre}</div>
                    <div className="text-xs text-codialis-gray">{etape.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mx-4 mb-4 rounded-xl bg-codialis-mint/10 p-3 text-xs text-codialis-navy sm:mx-6">
              Aucune réclamation n’est anonyme : chacune est rattachée à la personne qui l’a formulée, informée que son
              traitement est tracé. Délai visé : {compteurs?.delaiCible ?? '—'} jours, réglé par la direction.
            </p>
          </Carte>
        </div>
      </div>

      {ouverte && <FicheReclamation id={ouverte} options={data?.options} onFermer={() => ouvrir(null)} />}
      {saisie && data && (
        <SaisieReclamation
          options={data.options}
          siteParDefaut={site !== TOUS_LES_SITES && data.options.sites.some((ligne) => ligne.code === site) ? site : null}
          onFermer={() => setSaisie(false)}
          onEnregistree={(id) => {
            setSaisie(false);
            ouvrir(id);
          }}
        />
      )}
    </div>
  );
}

function LigneReclamation({ ligne, onOuvrir }: { ligne: LigneRegistre; onOuvrir: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOuvrir}
      className="flex w-full flex-col gap-2 px-4 py-4 text-left transition hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-4 sm:px-6"
    >
      <span className="shrink-0 font-mono text-xs text-codialis-gray sm:w-28">{ligne.reference}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-codialis-navy">{ligne.objet}</span>
        <span className="block text-xs text-codialis-gray">
          {ligne.nomReclamant}
          {ligne.entreprise ? ` · ${ligne.entreprise}` : ''} · {ligne.qualiteLibelle} · reçue le {dateReunion(ligne.recueLe)}
          {ligne.responsable ? ` · ${ligne.responsable.nom}` : ' · à assigner'}
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-1">
        <PastilleSite site={ligne.siteCode} />
        {ligne.delai.horsDelai && ligne.statut !== 'CLOTUREE' && <Pastille texte="Hors délai" ton="danger" />}
        <Pastille texte={ligne.statutLibelle} ton={tonStatut(ligne.statut)} />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mes réclamations (apprenant, formateur)
// ---------------------------------------------------------------------------

function MesReclamations(): JSX.Element {
  const { data, isLoading, error } = useMesReclamations(true);
  const [parametres] = useSearchParams();
  const ciblee = parametres.get('reclamation');

  return (
    <div className="mx-auto max-w-[1100px] space-y-6 p-4 sm:p-6">
      <EnteteEcran titre="Mes réclamations" sousTitre="Déposer une réclamation et suivre son traitement jusqu’à la réponse" />
      {isLoading && <Chargement libelle="Chargement de vos réclamations…" />}
      {error && <MessageErreur message={messageErreur(error, 'Vos réclamations n’ont pas pu être chargées.')} />}
      {data && (
        <>
          {data.refusDepot ? (
            <MessageErreur message={data.refusDepot} />
          ) : (
            <FormulaireDepot mention={data.mention} sites={data.sites} />
          )}
          <section aria-labelledby="titre-mes-reclamations" className="space-y-3">
            <h2 id="titre-mes-reclamations" className="font-display text-lg font-bold text-codialis-navy">
              Suivi de mes réclamations
            </h2>
            {data.reclamations.length === 0 ? (
              <p className="rounded-2xl bg-white px-4 py-10 text-center text-sm text-codialis-gray shadow-card">
                Vous n’avez déposé aucune réclamation.
              </p>
            ) : (
              <ul className="space-y-3">
                {data.reclamations.map((reclamation) => (
                  <li key={reclamation.id}>
                    <CarteMaReclamation reclamation={reclamation} ciblee={reclamation.id === ciblee} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function FormulaireDepot({ mention, sites }: { mention: string; sites: { code: string; nom: string }[] }): JSX.Element {
  const id = useId();
  const toast = useToast();
  const deposer = useDeposerReclamation();
  const vide = { objet: '', description: '', site: '', tracabiliteComprise: false };
  const [saisie, setSaisie] = useState(vide);
  const plusieursAntennes = sites.length > 1;
  const valide =
    saisie.objet.trim().length >= LIMITES_RECLAMATION.objet.min &&
    saisie.description.trim().length >= LIMITES_RECLAMATION.description.min &&
    saisie.tracabiliteComprise &&
    (!plusieursAntennes || saisie.site !== '');
  const classeChamp = 'mt-1 w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy';
  const classeLibelle = 'block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!valide) return;
    deposer.mutate(
      {
        objet: saisie.objet.trim(),
        description: saisie.description.trim(),
        tracabiliteComprise: true,
        ...(plusieursAntennes ? { site: saisie.site } : {}),
      },
      {
        onSuccess: (reponse) => {
          toast(`Réclamation ${reponse.reclamations[0]?.reference ?? ''} enregistrée`);
          setSaisie(vide);
        },
      },
    );
  };

  return (
    <Carte titre="Déposer une réclamation" sansBordure>
      <form onSubmit={envoyer} className="space-y-4 px-4 py-4 sm:px-6">
        <p className="flex items-start gap-2 rounded-xl bg-codialis-sand px-3 py-3 text-sm text-codialis-navy">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-codialis-teal" aria-hidden />
          <span className="min-w-0">{mention}</span>
        </p>
        {plusieursAntennes && (
          <div className="min-w-0">
            <label htmlFor={`${id}-site`} className={classeLibelle}>
              Antenne concernée
              <MarqueObligatoire />
            </label>
            <select
              id={`${id}-site`}
              required
              value={saisie.site}
              onChange={(evenement) => setSaisie((avant) => ({ ...avant, site: evenement.target.value }))}
              className={`${classeChamp} min-h-[44px] sm:w-80`}
            >
              <option value="">Choisir…</option>
              {sites.map((site) => (
                <option key={site.code} value={site.code}>
                  {site.nom}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="min-w-0">
          <label htmlFor={`${id}-objet`} className={classeLibelle}>
            Objet
            <MarqueObligatoire />
          </label>
          <input
            id={`${id}-objet`}
            type="text"
            required
            maxLength={LIMITES_RECLAMATION.objet.max}
            value={saisie.objet}
            onChange={(evenement) => setSaisie((avant) => ({ ...avant, objet: evenement.target.value }))}
            className={`${classeChamp} min-h-[44px]`}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={`${id}-description`} className={classeLibelle}>
            Votre réclamation
            <MarqueObligatoire />
          </label>
          <textarea
            id={`${id}-description`}
            required
            rows={5}
            maxLength={LIMITES_RECLAMATION.description.max}
            value={saisie.description}
            onChange={(evenement) => setSaisie((avant) => ({ ...avant, description: evenement.target.value }))}
            className={classeChamp}
          />
        </div>
        <label className="flex items-start gap-3 text-sm text-codialis-navy">
          <input
            type="checkbox"
            required
            checked={saisie.tracabiliteComprise}
            onChange={(evenement) => setSaisie((avant) => ({ ...avant, tracabiliteComprise: evenement.target.checked }))}
            className="mt-0.5 h-5 w-5 shrink-0"
          />
          <span className="min-w-0">
            J’ai compris que ma réclamation est enregistrée à mon nom et que son traitement est tracé.
            <MarqueObligatoire />
          </span>
        </label>
        {deposer.error && <MessageErreur message={messageErreur(deposer.error, 'Votre réclamation n’a pas pu être enregistrée.')} />}
        <div className="flex justify-end">
          <BoutonPrincipal type="submit" disabled={!valide || deposer.isPending}>
            {deposer.isPending ? 'Envoi…' : 'Déposer ma réclamation'}
          </BoutonPrincipal>
        </div>
      </form>
    </Carte>
  );
}

function CarteMaReclamation({ reclamation, ciblee }: { reclamation: MaReclamation; ciblee: boolean }): JSX.Element {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (ciblee) ref.current?.scrollIntoView({ block: 'start' });
  }, [ciblee]);

  return (
    <article
      ref={ref}
      aria-current={ciblee ? 'true' : undefined}
      className={`space-y-3 rounded-2xl bg-white p-4 shadow-card sm:p-5 ${ciblee ? 'ring-2 ring-codialis-sky' : ''}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="break-words font-display text-base font-bold text-codialis-navy">{reclamation.objet}</h3>
          <p className="text-xs text-codialis-gray">
            {reclamation.reference} · reçue le {dateReunion(reclamation.recueLe)}
          </p>
        </div>
        <Pastille texte={reclamation.statutLibelle} ton={tonStatut(reclamation.statut)} />
      </header>
      <p className="whitespace-pre-line break-words text-sm text-codialis-navy">{reclamation.description}</p>
      {reclamation.reponse && (
        <div className="rounded-xl border border-codialis-mint/40 bg-codialis-mint/10 px-3 py-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Réponse apportée{reclamation.clotureLe ? ` le ${dateReunion(reclamation.clotureLe)}` : ''}
          </p>
          <p className="mt-1 whitespace-pre-line break-words text-sm text-codialis-navy">{reclamation.reponse}</p>
        </div>
      )}
      <ol className="space-y-1 border-l-2 border-gray-100 pl-3">
        {reclamation.evenements.map((evenement, rang) => (
          <li key={`${evenement.le}-${rang}`} className="text-xs text-codialis-gray">
            <span className="font-semibold text-codialis-navy">{evenement.libelle}</span> · {horodatageReunion(evenement.le)}
          </li>
        ))}
      </ol>
    </article>
  );
}
